import { Tag } from '@ads/components-react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface PersonFormStepperStep {
  readonly key: string;
  readonly title: string;
}

export interface PersonFormStepperProps {
  /** Lista de etapas a exibir no stepper */
  readonly steps: ReadonlyArray<PersonFormStepperStep>;
  /** Índice da etapa atualmente ativa (0-based) */
  readonly currentStep: number;
  /** Número de erros de validação por chave de etapa */
  readonly errorCountByStep: Record<string, number>;
  /** Indica se o usuário já submeteu o formulário (controla exibição de erros) */
  readonly isSubmitted: boolean;
  /** Índice máximo desbloqueado — steps além deste ficam desabilitados até validação */
  readonly maxReachedStep: number;
  /** Callback chamado quando o usuário clica em uma etapa */
  readonly onStepClick: (index: number) => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Stepper horizontal customizado para o formulário de pessoa.
 *
 * Cada etapa exibe um círculo numerado (com check quando concluída), título
 * abaixo e tag de erro quando há campos pendentes. Conectores horizontais
 * entre círculos pintam-se na cor de marca conforme o progresso avança.
 */
export function PersonFormStepper({
  steps,
  currentStep,
  errorCountByStep,
  isSubmitted,
  maxReachedStep,
  onStepClick,
}: PersonFormStepperProps) {
  const { t } = useTranslation('personManagement');

  const navLabel = t('personForm.wizard.stepCounter', {
    current: currentStep + 1,
    total: steps.length,
  });

  return (
    <nav
      aria-label={navLabel}
      className="overflow-x-auto rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs"
    >
      <ol className="flex min-w-max items-start">
        {steps.map((step, i) => {
          const isCurrent = i === currentStep;
          const isCompleted = i < currentStep;
          const isLocked = i > maxReachedStep;
          const errorCount = errorCountByStep[step.key] ?? 0;
          const hasError = isSubmitted && errorCount > 0;
          const isFirst = i === 0;
          const isLast = i === steps.length - 1;

          // Cor dos conectores adjacentes ao círculo
          const leftConnectorBrand = i <= currentStep && !isFirst;
          const rightConnectorBrand = i < currentStep && !isLast;

          const circleClass =
            isCompleted || isCurrent
              ? 'bg-ctx-content-primary text-ctx-content-titleAlternative'
              : isLocked
                ? 'border-thin border-ctx-layout-border text-ctx-content-placeholder opacity-intense'
                : 'border-thin border-ctx-layout-border text-ctx-content-title';
          const titleClass = isCurrent
            ? 'text-ctx-content-title'
            : isCompleted
              ? 'text-ctx-content-base'
              : isLocked
                ? 'text-ctx-content-placeholder opacity-intense'
                : 'text-ctx-content-title';

          return (
            <Fragment key={step.key}>
              <li className="flex min-w-[5rem] flex-1 flex-col items-center gap-quark">
                <div className="flex w-full items-center">
                  <span
                    aria-hidden="true"
                    className={`h-[1px] flex-1 ${
                      isFirst
                        ? 'invisible'
                        : leftConnectorBrand
                          ? 'bg-ctx-highlight-ringFocusBorder'
                          : 'bg-ctx-layout-border'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => onStepClick(i)}
                    disabled={isLocked}
                    aria-current={isCurrent ? 'step' : undefined}
                    aria-label={step.title}
                    className={`relative z-10 flex h-xxxs w-xxxs shrink-0 items-center justify-center rounded-pill ts-caption-xxs transition-colors focus:outline-none focus:ring-[0.25rem] focus:ring-ctx-highlight-ringFocusShadow disabled:cursor-not-allowed ${circleClass}`}
                  >
                    {isCompleted ? <CheckIcon className="h-micro w-micro" /> : i + 1}
                  </button>
                  <span
                    aria-hidden="true"
                    className={`h-[1px] flex-1 ${
                      isLast
                        ? 'invisible'
                        : rightConnectorBrand
                          ? 'bg-ctx-highlight-ringFocusBorder'
                          : 'bg-ctx-layout-border'
                    }`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => onStepClick(i)}
                  disabled={isLocked}
                  className={`text-center ts-heading-xxs transition-colors focus:outline-none focus:ring-[0.25rem] focus:ring-ctx-highlight-ringFocusShadow disabled:cursor-not-allowed ${titleClass}`}
                >
                  {step.title}
                </button>
                {hasError && (
                  <Tag
                    size="sm"
                    status="negative"
                    label={t('personForm.wizard.stepHasErrors', {
                      count: errorCount,
                    })}
                    custom={{ text: 'text-nowrap' }}
                  />
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
